import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as S from '../form.styles';
import * as FormStyles from './diagnosis-codes-form.styles';
import { BaseInput } from '../../../base/input';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { FormErrorBanner } from '../../../base/form/form-error-banner';

interface IDiagnosisCodesFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const DiagnosisCodesForm: React.FunctionComponent<IDiagnosisCodesFormProps> = ({ claimEditorStore }) => {
    const initialCodes = useMemo<string[]>(() => {
        const payload: any = claimEditorStore?.activePayload;
        const services = payload?.services;
        const diagnosisCodes: string[] = [];
        if (Array.isArray(services)) {
            for (const service of services) {
                if (Array.isArray(service?.diagnosisCodes)) {
                    diagnosisCodes.push(...service.diagnosisCodes);
                }
            }
        }
        const unique = Array.from(new Set(diagnosisCodes)).filter(Boolean);
        const capped = unique.slice(0, 12);
        const padded = [...capped];
        while (padded.length < 12) padded.push('');
        return padded;
    }, [claimEditorStore?.activePayload]);

    const [codes, setCodes] = useState<string[]>(initialCodes);
    const saveTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        setCodes(initialCodes);
    }, [initialCodes]);

    const debouncedSave = useCallback(
        (updatedCodes: string[]) => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }

            saveTimerRef.current = setTimeout(() => {
     
                const filteredCodes = updatedCodes.filter(code => code.trim() !== '');
                
                const currentServices = claimEditorStore?.services || [];
                
                const updatedServices = currentServices.map((service: any) => ({
                    ...service,
                    diagnosisCodes: filteredCodes,
                }));
                
                claimEditorStore?.updateServices(updatedServices);
            }, 500);
        },
        [claimEditorStore],
    );

    useEffect(() => {
        const hasChanged = JSON.stringify(codes) !== JSON.stringify(initialCodes);
        if (hasChanged) {
            debouncedSave(codes);
        }
    }, [codes, debouncedSave, initialCodes]);

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, []);

    const handleChange = (idx: number, value: string) => {
        setCodes(prev => {
            const next = [...prev];
            next[idx] = value.toUpperCase();
            return next;
        });
    };

    const firstEmptyIndex = useMemo(() => codes.findIndex(v => !v), [codes]);
    const firstCodeIsEmpty = useMemo(() => !codes[0] || codes[0].trim() === '', [codes]);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        if (claimEditorStore.validationSignal > 0) {
            setShowError(firstCodeIsEmpty);
        }
    }, [claimEditorStore.validationSignal, firstCodeIsEmpty]);

    return (
        <S.FormContainer>
            {showError && (
                <FormErrorBanner messages={['At least one DX code is required']} />
            )}
            <FormStyles.CodesGrid>
                {codes.map((code, idx) => {
                    const isEmpty = !code;
                    const enabled = !isEmpty || idx === firstEmptyIndex;
                    const isFirst = idx === 0;
                    return (
                        <S.FormField key={idx}>
                            <S.Label required={isFirst}>{`DX Code ${idx + 1}`}</S.Label>
                            <BaseInput
                                name={`dx_${idx}`}
                                placeholder={`DX Code ${idx + 1}`}
                                value={code}
                                onChange={e => {
                                    handleChange(idx, e.target.value);
                                    if (isFirst && showError) {
                                        setShowError(false);
                                    }
                                }}
                                disabled={!enabled}
                            />
                        </S.FormField>
                    );
                })}
            </FormStyles.CodesGrid>
        </S.FormContainer>
    );
};

export default DiagnosisCodesForm;


